/**
 * The structure of the SIFT worksheet.
 *
 * SIFT is Mike Caulfield's four-move routine for judging online information quickly:
 * Stop; Investigate the source; Find better coverage; Trace claims, quotes and media to
 * the original context ("SIFT (The Four Moves)", hapgood.us, 19 June 2019, CC BY 4.0).
 * The moves and their names are his. The questions in the worksheet are this site's
 * prompts for carrying them out, not his words.
 *
 * Fields are data: a key, a kind and, for choices, the option keys. The widget supplies
 * the wording for each, so a translation can reach every label.
 */

export type FieldKind = "text" | "area" | "choice";

export interface SiftField {
  key: string;
  kind: FieldKind;
  /** For a choice: the option keys, in the order shown. */
  options?: readonly string[];
}

export interface SiftMove {
  key: "stop" | "investigate" | "find" | "trace";
  fields: readonly SiftField[];
}

export const SIFT_MOVES: readonly SiftMove[] = [
  {
    key: "stop",
    fields: [
      { key: "stopKnow", kind: "choice", options: ["yes", "unsure", "no"] },
      { key: "stopPurpose", kind: "area" },
      { key: "stopFeeling", kind: "choice", options: ["calm", "strong"] },
    ],
  },
  {
    key: "investigate",
    fields: [
      { key: "invWho", kind: "text" },
      { key: "invExpertise", kind: "area" },
      { key: "invAgenda", kind: "area" },
      { key: "invChecked", kind: "area" },
      {
        key: "invVerdict",
        kind: "choice",
        options: ["yes", "caution", "no", "unclear"],
      },
    ],
  },
  {
    key: "find",
    fields: [
      { key: "claim", kind: "area" },
      { key: "betterSources", kind: "area" },
      {
        key: "consensus",
        kind: "choice",
        options: ["agrees", "disagrees", "divided", "none"],
      },
    ],
  },
  {
    key: "trace",
    fields: [
      { key: "original", kind: "area" },
      { key: "foundOriginal", kind: "choice", options: ["yes", "no"] },
      {
        key: "context",
        kind: "choice",
        options: ["accurate", "partly", "no", "unclear"],
      },
      { key: "traceNotes", kind: "area" },
    ],
  },
];

/** The closing questions, after the four moves. */
export const SIFT_CONCLUSION: readonly SiftField[] = [
  {
    key: "verdict",
    kind: "choice",
    options: ["supported", "partly", "unsupported", "unverified"],
  },
  {
    key: "nextStep",
    kind: "choice",
    options: ["use", "caveats", "better", "drop"],
  },
  { key: "finalNotes", kind: "area" },
];

/** The three lines at the top of the worksheet. */
export const SIFT_HEADER: readonly SiftField[] = [
  { key: "topic", kind: "area" },
  { key: "where", kind: "text" },
];

export type SiftState = Readonly<Record<string, string>>;

export function allFields(): SiftField[] {
  return [
    ...SIFT_HEADER,
    ...SIFT_MOVES.flatMap((move) => move.fields),
    ...SIFT_CONCLUSION,
  ];
}

/** Fields with an answer (text typed or an option chosen). */
export function answeredCount(state: SiftState): number {
  return allFields().filter((field) => (state[field.key] ?? "").trim() !== "")
    .length;
}

export function fieldCount(): number {
  return allFields().length;
}

/** Answered fields in one move, out of its total. */
export function moveProgress(
  state: SiftState,
  move: SiftMove
): { answered: number; total: number } {
  return {
    answered: move.fields.filter(
      (field) => (state[field.key] ?? "").trim() !== ""
    ).length,
    total: move.fields.length,
  };
}
